import React, { useEffect } from "react";
import { TruckIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";

import {
  getNotifications,
  readNotification,
  markForDelete,
  deleteNotification,
  clearPendingDeletes,
  deleteAllNotifications,
} from "@/store/slices/notificationSlice";
import { getUserProfile } from "@/store/slices/userSlice";
import ShowLoader from "@/components/Loader";

// Delivery Partner Notification Page
export default function NotificationsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation("delivery");

  const { notifications, loading } = useSelector(
    (state) => state.notification
  );

  useEffect(() => {
    dispatch(getNotifications({ page: 1 }));
  }, [dispatch]);

  const handleDeleteAll = () => {
    Swal.fire({
      title: t("are_you_sure"),
      text: t("delete_all_notifications_text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#065C36",
      cancelButtonColor: "#d33",
      confirmButtonText: t("yes_delete_all"),
      cancelButtonText: t("cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteAllNotifications())
          .unwrap()
          .then((res) => {
            if (res.status) {
              Swal.fire(
                t("deleted"),
                t("all_notifications_cleared"),
                "success"
              );
              dispatch(getNotifications({ page: 1 }));
            }
          })
          .catch((err) => {
            Swal.fire(
              t("error"),
              err?.message || t("something_went_wrong"),
              "error"
            );
          });
      }
    });
  };

  const handleNavigation = (notification) => {
    const type = notification?.type;

    const routes = {
      "delivery_order_assigned": "/delivery-partner/new-deliveries",
    };

    const targetRoute = routes[type];

    if (targetRoute) {
      navigate(targetRoute);
    }

    // Mark as read when navigating
    if (notification?.status === "unread") {
      dispatch(readNotification({ ids: [notification.id] })).unwrap().then((res) => {
          if (res?.status) {
              dispatch(getUserProfile());
              dispatch(getNotifications({ page: 1 }));
          }
      });
    }
  };

  const notificationList =
    notifications?.data?.notifications ||
    notifications?.notifications ||
    [];

  return (
    <div className="min-h-screen flex flex-col bg-gray-50/80">
      {loading && notificationList.length === 0 && <ShowLoader />}

      {/* HEADER */}
      <div className="w-full bg-[#065C36] py-5 sm:py-6 text-white px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          {/* Spacer to keep title centered */}
          <div className="w-10 hidden sm:block"></div>

          <h1 className="text-xl sm:text-2xl font-semibold">
            {t("notifications")}
          </h1>

          {/* Delete All Button */}
          {notificationList.length > 0 ? (
            <button
              onClick={handleDeleteAll}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors px-3 py-1.5 rounded-lg text-sm font-medium border border-white/20"
            >
              <TrashIcon className="w-4 h-4" />
              <span className="hidden xs:inline">{t("clear_all")}</span>
            </button>
          ) : (
            <div className="w-10"></div>
          )}
        </div>
      </div>

      {/* CONTENT */}
      <div className="w-full max-w-7xl mx-auto py-6 sm:py-10 px-4 sm:px-6 space-y-4 sm:space-y-6 flex-1">

        {!loading && notificationList.length === 0 && (
          <p className="text-center text-gray-400 py-12 rounded-xl border border-dashed border-gray-200 bg-white">
            {t("no_notifications_found")}
          </p>
        )}

        {notificationList.map((item) => (
          <NotificationCard
            key={item.id}
            data={item}
            onNavigate={() => handleNavigation(item)}
          />
        ))}
      </div>
    </div>
  );
}

/* ================= CARD ================= */

function NotificationCard({ data, onNavigate }) {
  const dispatch = useDispatch();
  const { t } = useTranslation("delivery");
  const isUnread = data?.status === "unread";

  const handleCardClick = () => {
    onNavigate();
  };

  const handleDelete = (e) => {
    e.stopPropagation();

    Swal.fire({
      title: t("are_you_sure"),
      text: t("notification_delete_text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#065C36",
      cancelButtonColor: "#d33",
      confirmButtonText: t("yes_delete_it"),
      cancelButtonText: t("cancel"),
    }).then((result) => {
      if (result.isConfirmed) {
        // Proceed with deletion if confirmed
        dispatch(markForDelete(data.id));
        dispatch(deleteNotification([data.id]))
          .unwrap()
          .then(() => {
            dispatch(clearPendingDeletes());

            // Optional: Success toast
            Swal.fire({
              title: t("deleted"),
              text: t("notification_removed"),
              icon: "success",
              timer: 800,
              showConfirmButton: false
            });
          })
          .catch(() => {
            // Re-fetch list if API fails to sync state
            dispatch(getNotifications({ page: 1 }));
          });
      }
    });
  };

  return (
    <div
      onClick={handleCardClick}
      className={`relative rounded-xl border p-5 transition-all duration-200 cursor-pointer
      ${
        isUnread
          ? "bg-[#FDF3DD] border-[#F4B11F]/40"
          : "bg-white border-gray-200"
      }
      hover:shadow-md`}
    >
      <div className="flex items-start gap-4">

        {/* Icon */}
        <div
          className={`p-2 rounded-lg
          ${isUnread ? "bg-[#0E5C36]/10" : "bg-gray-100"}`}
        >
          <TruckIcon
            className={`w-5 h-5
            ${isUnread ? "text-[#0E5C36]" : "text-gray-400"}`}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-800">
            {data?.title}
          </h3>
          <p className="text-sm text-gray-600 mt-1 break-words [overflow-wrap:anywhere]">
            {data?.message}
          </p>
          <p className="text-xs text-gray-400 mt-2">
            {moment(data?.created_at).fromNow()}
          </p>
        </div>

        {/* Unread Dot */}
        {isUnread && (
          <span className="w-2.5 h-2.5 bg-[#0E5C36] rounded-full mt-2" />
        )}

        {/* Delete */}
        <button
          onClick={handleDelete}
          className="shrink-0 text-red-500 text-sm hover:scale-125 transition-transform p-1"
        >
          ✕
        </button>
      </div>
    </div>
  );
}